import { AsyncStatus } from 'lib-shared-legacy-types';
import type { Action } from 'redux-actions';
import { handleActions } from 'redux-actions';

import { flowLanguagesActions } from '../actions/flowLanguages';
import { ChatFlowGetAllLanguagesResponseT } from '../api/types';
import { Gender, LanguageCode, LanguagesT } from '../components/types';

const { flowGetLanguages, flowGetLanguagesSucceeded, flowGetLanguagesFailed } =
  flowLanguagesActions;

export type FlowLanguagesStateT = {
  meta: {
    getAll: AsyncStatus;
  };
  languages: LanguageT[];
};

export type LanguageT = {
  label: string;
  type: string;
  value: string;
};

type LanguageCodeLabelT = Record<LanguageCode, string>;

const langCodeToLabel: LanguageCodeLabelT = {
  [LanguageCode.ARB]: 'Arabic',
  [LanguageCode.CMN_CN]: 'Chinese (Mandarin)',
  [LanguageCode.CS_CZ]: 'Czech',
  [LanguageCode.DA_DK]: 'Danish',
  [LanguageCode.DE_DE]: 'German',
  [LanguageCode.EN_GB]: 'English (GB)',
  [LanguageCode.EN_US]: 'English (US)',
  [LanguageCode.ES_ES]: 'Spanish',
  [LanguageCode.ET_EE]: 'Estonian',
  [LanguageCode.FR_FR]: 'French',
  [LanguageCode.IS_IS]: 'Icelandic',
  [LanguageCode.IT_IT]: 'Italian',
  [LanguageCode.JA_JP]: 'Japanese',
  [LanguageCode.KO_KR]: 'Korean',
  [LanguageCode.LT_LT]: 'Lithuanian',
  [LanguageCode.LV_LV]: 'Latvian',
  [LanguageCode.NB_NO]: 'Norwegian',
  [LanguageCode.NL_NL]: 'Dutch',
  [LanguageCode.PL_PL]: 'Polish',
  [LanguageCode.PT_PT]: 'Portuguese',
  [LanguageCode.RO_RO]: 'Romanian',
  [LanguageCode.RU_RU]: 'Russian',
  [LanguageCode.SV_SE]: 'Swedish',
  [LanguageCode.TR_TR]: 'Turkish',
  [LanguageCode.FI_FI]: 'Finnish',
  [LanguageCode.YUE_CN]: 'Chinese (Cantonese)',
};

// Init state for the Chat Flow.
const initialState: FlowLanguagesStateT = {
  meta: {
    getAll: 'succeeded',
  },
  languages: [],
};

// Reducer
const flowLanguages = handleActions<
  FlowLanguagesStateT,
  ChatFlowGetAllLanguagesResponseT
>(
  {
    [flowGetLanguages.toString()]: (
      state: FlowLanguagesStateT,
    ): FlowLanguagesStateT => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getAll: 'loading',
        },
      };
    },

    [flowGetLanguagesSucceeded.toString()]: (
      state: FlowLanguagesStateT,
      action: Action<ChatFlowGetAllLanguagesResponseT>,
    ): FlowLanguagesStateT => {
      const hasPayload = action && action.payload;

      const allLanguages: LanguagesT = hasPayload
        ? Object.assign({}, action.payload.data.languages)
        : { [LanguageCode.EN_GB]: [Gender.Female, Gender.Male] };
      const languages: LanguageT[] = [];

      for (const languageKey in allLanguages) {
        const language = langCodeToLabel[languageKey] || '';
        for (const type of allLanguages[languageKey]) {
          if (language !== '') {
            languages.push({
              label: `${language} (${type} voice)`,
              type,
              value: languageKey,
            });
          }
        }
      }

      return {
        ...state,
        meta: {
          ...state.meta,
          getAll: 'succeeded',
        },
        languages,
      };
    },

    [flowGetLanguagesFailed.toString()]: (
      state: FlowLanguagesStateT,
    ): FlowLanguagesStateT => {
      return {
        ...state,
        meta: {
          ...state.meta,
          getAll: 'failed',
        },
      };
    },
  },
  initialState,
);

export default flowLanguages;
